import { MenuItem, type SxProps, type Theme } from "@mui/material";
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { INK } from "@/theme/tokens";

type MenuItemWithReasonProps = {
	/**
	 * Present ⇒ the item is unavailable and this is the tooltip explaining why. Absent ⇒ a plain,
	 * enabled `MenuItem`. Making the reason the enable/disable switch is the point: it is impossible
	 * to disable this item without saying why.
	 */
	disabledReason?: ReactNode;
	onClick: (event: MouseEvent<HTMLLIElement>) => void;
	children: ReactNode;
	dataTestId?: string;
	/**
	 * The row's own geometry and ink (a menu with roomier rows, icons, a tone) — the same sx in both states, so
	 * an available and an unavailable row line up. The unavailable look is layered on top of it.
	 */
	sx?: SxProps<Theme>;
};

/**
 * A menu item that can only be made unavailable *with a stated reason*.
 *
 * Every call site that tried this by hand wrote `Tooltip > span > MenuItem disabled`, which has two
 * faults: a `span` is not a `menuitem`, so it breaks the `role="menu"` contract and is opaque to
 * MUI's `MenuList` (which clones its direct children to manage focus) — and a truly `disabled`
 * MenuItem leaves the tab order, so the reason it exists to give never reaches a keyboard user.
 * This renders `aria-disabled` instead: a real, focusable `menuitem` that announces as unavailable
 * and whose tooltip opens on hover *and* focus.
 */
export const MenuItemWithReason: FC<MenuItemWithReasonProps> = ({ disabledReason, onClick, children, dataTestId, sx }) => {
	const ownSx = sx === undefined ? [] : Array.isArray(sx) ? sx : [sx];
	if (!disabledReason) {
		return (
			<MenuItem onClick={onClick} data-testid={dataTestId} sx={ownSx}>
				{children}
			</MenuItem>
		);
	}

	// Swallow both activation paths. `aria-disabled` is a promise to assistive tech, not an
	// enforcement, so the item has to decline the action itself.
	const swallowClick = (event: MouseEvent<HTMLLIElement>) => event.stopPropagation();
	const swallowActivationKeys = (event: KeyboardEvent<HTMLLIElement>) => {
		if (event.key === "Enter" || event.key === " ") event.preventDefault();
	};

	return (
		<InHouseTooltip title={disabledReason}>
			<MenuItem
				aria-disabled
				onClick={swallowClick}
				onKeyDown={swallowActivationKeys}
				sx={[...ownSx, { color: INK.DISABLED, cursor: "default", "&:hover": { backgroundColor: "transparent" } }]}
				data-testid={dataTestId}
			>
				{children}
			</MenuItem>
		</InHouseTooltip>
	);
};
